import { clsx } from 'clsx'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import Pica from 'pica'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { fixImageUrl, proxyImageUrl } from 'util/image'
import { delay } from 'util/promise'
import { getStorageItem } from 'util/storage'
import css from './PicaImage.module.scss'

const PICA = false

type Props = {
  className?: string
  src: string
  onLoad?(event: SyntheticEvent<HTMLImageElement>): void
  onError?(event: SyntheticEvent<HTMLImageElement>): void
  onRef?(element: HTMLImageElement | null): void
}

export const PicaImage = (props: Props) => {
  const className = clsx(props.className, css.image)
  const src = PICA ? proxyImageUrl(props.src) : fixImageUrl(props.src)
  const [scaled, setScaled] = useState('')
  const refImage = useRef<HTMLImageElement>()

  const scale = useLatestCallback(async (image: HTMLImageElement, src: string) => {
    function ok() {
      return image.parentElement && image.src === src
    }

    while (ok() && !image.complete) {
      await delay(100)
    }

    if (!ok()) return

    URL.revokeObjectURL(scaled)
    setScaled('')

    const canvas = document.createElement('canvas')
    const bound = image.getBoundingClientRect()
    const aspect = image.naturalWidth / image.naturalHeight
    const width = Math.max(bound.width, bound.height * aspect)
    const height = Math.max(bound.height, bound.width / aspect)
    canvas.width = width
    canvas.height = height

    const start = Date.now()
    const pica = Pica({})
    const result = await pica.resize(image, canvas)
    if (!ok()) return
    const blob = await pica.toBlob(result, 'image/jpeg', 0.9)
    if (!ok()) return
    const url = URL.createObjectURL(blob)
    setScaled(url)
    if (getStorageItem('debug')) {
      const size = image.naturalWidth + 'x' + image.naturalHeight
      console.info('pica', size, Date.now() - start)
    }
  })

  const onRef = useLatestCallback((element: HTMLImageElement | null) => {
    refImage.current = element ?? undefined
    if (props.onRef) props.onRef(element)
  })

  useEffect(() => {
    const image = refImage.current
    if (PICA && image) scale(image, src)
  }, [scale, src])

  useEffectOnce(() => {
    return () => URL.revokeObjectURL(scaled)
  })

  return <img ref={onRef} className={className} src={scaled || src} alt=""
    onLoad={props.onLoad} onError={props.onError} />
}
